import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastService } from 'on-common/_services/toast.service';
import { AccountingPlan } from 'on-shared/_models/AccountingPlan';
import { Company } from 'on-shared/_models/Company';
import { EmployeeCategory } from 'on-shared/_models/EmployeeCategory';
import { OnUser } from 'on-shared/_models/OnUser';
import { UserPrefill } from 'on-shared/_models/UserPrefill';
import {
    LanguageDetectionService,
    LanguageInfo,
} from 'on-shared/_services/language-detection.service';
import { AccountingPlanAction } from 'on-shared/stores/accounting-plan/accounting-plan.action';
import { AccountingPlanState } from 'on-shared/stores/accounting-plan/accounting-plan.state';
import { CompanyAction } from 'on-shared/stores/company/company.action';
import { CompanyService } from 'on-shared/stores/company/company.service';
import { CompanyState } from 'on-shared/stores/company/company.state';
import { UserService } from 'on-shared/stores/user/user.service';

/**
 * Profile an administrator prepares for the people they invite.
 *
 * Every field is optional: a field left untouched is sent as null so that the
 * server leaves alone whatever the invited person may already have.
 */
@Component({
    selector: 'on-invitation-prefill-form',
    templateUrl: './invitation-prefill-form.component.html',
    styleUrls: ['./invitation-prefill-form.component.scss'],
})
export class InvitationPrefillFormComponent implements OnInit, OnDestroy {
    /**
     * The employees of the company, used to pick a manager.
     */
    @Input() allEmployees: OnUser[] = [];

    /**
     * False when the same profile is applied to several invitations at once:
     * the fields that only make sense for a single person are then hidden.
     */
    @Input() showPersonalFields = true;

    /**
     * Profile already prepared for this invitation, when editing one.
     */
    @Input() prefill: UserPrefill;

    prefillForm: UntypedFormGroup;

    allRoles: string[] = [];
    allLanguages: LanguageInfo[] = [];

    @Select(CompanyState.employeeCategories)
    employeeCategories$: Observable<EmployeeCategory[]>;
    @Select(CompanyState.current) company$: Observable<Company>;
    @Select(AccountingPlanState.userAccountingPlans)
    accountingPlans$: Observable<AccountingPlan[]>;

    selectedAccountingPlans: { [planId: number]: number } = {};

    /**
     * Employee whose settings are used as a starting point for the profile.
     */
    copyFromEmployeeId: number = null;
    isCopying = false;

    /**
     * The accounting plans are only sent once the administrator touched them:
     * an empty selection clears the values, so we must not send one by default.
     */
    accountingPlansTouched = false;

    destroy$ = new Subject<void>();

    /**
     * Fields that describe one person and cannot be shared between several
     * invitations.
     */
    private readonly personalFields = [
        'FirstName',
        'LastName',
        'PhoneNumber',
        'ContactEmail',
        'Identifier',
        'ThirdPartyAccount',
        'DistanceHomeToWork',
    ];

    constructor(
        private fb: UntypedFormBuilder,
        private store: Store,
        private companyService: CompanyService,
        private languageDetectionService: LanguageDetectionService,
        private userService: UserService,
        private toastService: ToastService,
        private translateService: TranslateService,
    ) {}

    ngOnInit() {
        this.buildForms();

        this.allLanguages =
            this.languageDetectionService.getSupportedLanguages();

        this.companyService
            .GetRoles()
            .pipe(takeUntil(this.destroy$))
            .subscribe((roles) => (this.allRoles = roles || []));

        this.store.dispatch(new CompanyAction.LoadEmployeeCategoriesIfNeeded());
        this.store.dispatch(new AccountingPlanAction.LoadAccountingPlans());

        this.patchValues();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    accountingPlanChanged() {
        this.accountingPlansTouched = true;
    }

    getEmployeeLabel(employee: OnUser): string {
        return (
            [employee.FirstName, employee.LastName]
                .filter((part) => !!part)
                .join(' ') || employee.UserName
        );
    }

    /**
     * Start the profile from the settings of someone already in the company.
     * Only what describes the job is copied, never what describes the person.
     */
    copyFromEmployee() {
        if (!this.copyFromEmployeeId) {
            return;
        }

        this.isCopying = true;
        this.userService.GetUser(this.copyFromEmployeeId).subscribe(
            (response) => {
                this.isCopying = false;
                if (response.IsSuccess && response.Result) {
                    this.applyEmployeeSettings(new OnUser(response.Result));
                } else {
                    this.displayCopyError();
                }
            },
            () => {
                this.isCopying = false;
                this.displayCopyError();
            },
        );
    }

    isValid(): boolean {
        return this.prefillForm.valid;
    }

    /**
     * Build the profile to send, leaving out everything the administrator did
     * not fill.
     */
    buildPrefill(): UserPrefill {
        const prefill = new UserPrefill();

        for (const field of Object.keys(this.prefillForm.controls)) {
            if (!this.showPersonalFields && this.isPersonalField(field)) {
                continue;
            }

            prefill[field] = this.normalize(this.prefillForm.get(field).value);
        }

        prefill.AccountingPlanValues = this.buildAccountingPlanValues();

        return prefill;
    }

    private buildForms() {
        this.prefillForm = this.fb.group({
            FirstName: [null],
            LastName: [null],
            PhoneNumber: [null],
            ContactEmail: [null, Validators.email],
            LanguageCode: [null],
            MileageRatio: [null],
            DistanceHomeToWork: [null],
            Role: [null],
            ManagerId: [null],
            EmployeeCategoryId: [null],
            Identifier: [null],
            OrganizationUnit: [null],
            OtherInfo1: [null],
            OtherInfo2: [null],
            OtherInfo3: [null],
            OtherInfo4: [null],
            ThirdPartyAccount: [null],
            HasRestaurantTicket: [null],
            HasPerDiem: [null],
            HasProfessionalCard: [null],
        });
    }

    private patchValues() {
        if (!this.prefill) {
            return;
        }

        this.prefillForm.patchValue(this.prefill);

        if (this.prefill.AccountingPlanValues) {
            this.accountingPlansTouched = true;
            for (const value of this.prefill.AccountingPlanValues) {
                this.selectedAccountingPlans[value.AccountingPlanId] =
                    value.AccountingPlanValueId;
            }
        }
    }

    private applyEmployeeSettings(employee: OnUser) {
        const settings = {
            LanguageCode: employee.LanguageCode,
            MileageRatio: employee.MileageRatio,
            Role: employee.Role,
            ManagerId: employee.ManagerId,
            EmployeeCategoryId: employee.EmployeeCategory
                ? employee.EmployeeCategory.Id
                : null,
            OrganizationUnit: employee.OrganizationUnit,
            OtherInfo1: employee.OtherInfo1,
            OtherInfo2: employee.OtherInfo2,
            OtherInfo3: employee.OtherInfo3,
            OtherInfo4: employee.OtherInfo4,
            HasRestaurantTicket: employee.HasRestaurantTicket,
            HasPerDiem: employee.HasPerDiem,
            HasProfessionalCard: employee.HasProfessionalCard,
        };

        // Guard against copying anything that identifies the employee, even if
        // the list above grows later on
        for (const field of Object.keys(settings)) {
            if (this.isPersonalField(field)) {
                delete settings[field];
            }
        }

        this.prefillForm.patchValue(settings);

        this.selectedAccountingPlans = {};
        for (const value of employee.AccountingPlanValues || []) {
            this.selectedAccountingPlans[value.AccountingPlanId] =
                value.AccountingPlanValueId;
        }
        // The copied profile is meant to match the employee, including when
        // they have no analytic value at all
        this.accountingPlansTouched = true;
    }

    private displayCopyError() {
        this.toastService.error(
            this.translateService.instant(
                'company.employees.add-employee.prefill.copy-error',
            ),
        );
    }

    private isPersonalField(field: string): boolean {
        return this.personalFields.indexOf(field) !== -1;
    }

    /**
     * An untouched input gives an empty string, which would erase the value the
     * invited person may already have: turn it back into "not filled".
     */
    private normalize(value: unknown): unknown {
        if (typeof value === 'string') {
            const trimmed = value.trim();
            return trimmed.length > 0 ? trimmed : null;
        }

        return value === undefined ? null : value;
    }

    private buildAccountingPlanValues() {
        if (!this.accountingPlansTouched) {
            return null;
        }

        return Object.keys(this.selectedAccountingPlans)
            .filter((planId) => !!this.selectedAccountingPlans[planId])
            .map((planId) => ({
                AccountingPlanId: +planId,
                AccountingPlanValueId: this.selectedAccountingPlans[planId],
            }));
    }
}
